import { Conversation, Message } from '@nextforce/api'
import { Button, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import ConversationCard from 'chat/conversations/views/card'
import React, { useState } from 'react'
import { SectionList, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { MessagesSearchBar } from './search_bar'

interface Props {
  conversations: Conversation[] | undefined
  messages: Message[] | undefined
  onPressConversation(conversation: Conversation, messageId?: string): void
  cachedDays: number
  onLoadOlderMessages: () => Promise<void>
  isLoadingOlder: boolean
}

export function SearchView({
  conversations,
  messages,
  onPressConversation,
  cachedDays,
  onLoadOlderMessages,
  isLoadingOlder,
}: Props) {
  const styles = useStyles()

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [filteredConversationName, setFilteredConversationName] = useState<
    Conversation[]
  >(conversations ?? [])

  const [filteredMessages, setFilteredMessages] = useState<Conversation[]>([])
  const [filteredSenders, setFilteredSenders] = useState<Conversation[]>([])

  if (!conversations || !messages) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <MessagesSearchBar
          conversations={conversations}
          messages={messages}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onChangeConversationName={setFilteredConversationName}
          onChangeMessage={setFilteredMessages}
          onChangeSender={setFilteredSenders}
        />

        {searchTerm && (
          <View style={styles.searchInfoBanner}>
            <Text style={styles.searchInfoText}>
              {t('searchShowingResultsFromDays', { count: cachedDays })}
            </Text>
            <Button
              title={t('loadEarlierMessages')}
              onPress={onLoadOlderMessages}
              disabled={isLoadingOlder}
              loading={isLoadingOlder}
              buttonStyle={styles.loadOlderButton}
              titleStyle={styles.loadOlderButtonText}
              type="outline"
            />
          </View>
        )}

        {searchTerm ? (
          <SectionList
            keyboardDismissMode="on-drag"
            keyExtractor={item => item.id + '' + item.status?.recentMessage?.id}
            stickySectionHeadersEnabled={false}
            renderSectionHeader={({ section }) =>
              section.data.length > 0 ? (
                <Text style={styles.sectionHeader}>{section.title}</Text>
              ) : null
            }
            sections={[
              { title: 'CONVERSATIONS', data: filteredConversationName },
              { title: 'MESSAGES', data: filteredMessages },
              { title: 'SENDER', data: filteredSenders },
            ]}
            renderItem={({ item, section }) => (
              <ConversationCard
                conversation={item}
                user={undefined}
                onPressConversation={onPressConversation}
                onLongPress={() => null}
                highlightSenderName={section.title === 'SENDER' ? true : false}
              />
            )}
          />
        ) : null}
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors?.white,
    padding: 10,
  },
  sectionHeader: {
    fontSize: 13,
    color: theme.colors?.grey1,
    marginTop: 10,
    marginBottom: 10,
    marginLeft: 12,
  },
  searchInfoBanner: {
    backgroundColor: theme.colors?.grey5,
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 60,
  },
  searchInfoText: {
    fontSize: 13,
    color: theme.colors?.grey2,
    flex: 1,
    marginRight: 8,
  },
  loadOlderButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    width: 150,
  },
  loadOlderButtonText: {
    fontSize: 12,
  },
}))
